import React from "react"

import { DocumentArrowDownIcon } from "@heroicons/react/24/solid"

import classNames from "src/classNames"

import ExpandableImageCollection from "components/ExpandableImageCollection"
import LocalTime from "components/LocalTime"
import Avatar from "components/Messaging/Avatar"

import ReportMessageButton from "./ReportMessageButton"

export type MessageProps = {
  viewer: {
    id: string
    type: "Teacher" | "Tutor" | "Student" | "Parent"
  }
  message: {
    id: string
    read?: boolean
    content: string
    createdAt: string
    contentFlagged: boolean
    author: {
      id: string
      type: "Teacher" | "Student" | "Tutor" | "Parent"
      messagingDisplayName: string
      avatar: {
        thumbUrl
      }
    }
    attachment?: {
      filename: string
      downloadUrl: string
      url: string
      thumbUrl: string
      previewUrl: string
      contentType: string
    }
  }
}

const Message: React.FC<MessageProps> = ({ message, viewer }) => {
  const {
    id,
    content,
    attachment,
    author,
    createdAt,
    contentFlagged,
    read = true,
  } = message

  return (
    <div className="mb-6 border-0 border-b-[1px] border-solid border-b-slate-300 pb-2 sm:flex">
      <div className="mb-4 shrink-0 sm:mb-0 sm:mr-4">
        <Avatar
          size="medium"
          src={author.avatar.thumbUrl}
          alt={author.messagingDisplayName}
        />
      </div>
      <div className="w-full">
        <div className="space-between flex w-full items-center">
          <div className="flex grow items-center gap-4 sm:flex-row">
            <h4 className="text-lg font-bold">
              {author.id === viewer.id ? "You" : author.messagingDisplayName}
            </h4>
            <span className="italic text-slate-400">
              <LocalTime timestamp={createdAt} relative />
            </span>
            <span className="text-sm text-slate-400">
              <LocalTime timestamp={createdAt} />
            </span>
          </div>
          {author.id !== viewer.id && !contentFlagged && (
            <ReportMessageButton messageId={id} userType={viewer.type} />
          )}
        </div>

        <p className={classNames("mt-1", !read && "font-bold")}>{content}</p>
        {attachment?.downloadUrl && (
          <>
            {attachment.contentType.includes("image/") && (
              <ExpandableImageCollection images={[attachment]} />
            )}
            <div className="my-2">
              <a
                href={attachment.downloadUrl}
                className="flex items-center font-medium text-brand-600"
              >
                <DocumentArrowDownIcon className="mr-2 h-5 w-5" />
                <span>Download {attachment.filename}</span>
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default Message
